import { useEffect, useRef, useState, useCallback } from "react";
import { v4 as uuid } from "uuid";
import api from "@/lib/api";

const ICE = { iceServers: [{ urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] }] };

export const ROLE_LABEL = {
  host: "Host", cohost: "Co-host", moderator: "Moderator",
  presenter: "Presenter", panelist: "Panelist", attendee: "Attendee",
};
const MANAGE = ["host", "cohost", "moderator"];
const ADMIN = ["host", "cohost"];
const STAGE = ["host", "cohost", "presenter", "panelist"];

// Capabilities derived from a role within a given meeting mode.
export function roleCaps(role, mode) {
  const r = role || "panelist";
  const open = ["meet", "conference", "private"].includes(mode);
  return {
    manage: MANAGE.includes(r),
    admin: ADMIN.includes(r),
    share: ADMIN.includes(r) || r === "presenter" || open,
    onStage: STAGE.includes(r),
    canSpeakByRole: r !== "attendee",
  };
}

let _mpPromise;
function loadMP() {
  if (_mpPromise) return _mpPromise;
  _mpPromise = new Promise((res, rej) => {
    if (window.SelfieSegmentation) return res();
    const s = document.createElement("script");
    s.src = "https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/selfie_segmentation.js";
    s.crossOrigin = "anonymous"; s.async = true;
    s.onload = () => res(); s.onerror = rej;
    document.head.appendChild(s);
  });
  return _mpPromise;
}

function wsUrl(code) {
  const base = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
  return `${base}/api/ws/meet/${code}`;
}

// A stable identity per room so a refresh/rejoin re-applies the same DB role.
function stablePid(code, roleKey) {
  if (roleKey) return String(roleKey);
  const k = `meetpid:${code}`;
  let v = localStorage.getItem(k);
  if (!v) { v = uuid(); localStorage.setItem(k, v); }
  return v;
}

// Real WebRTC mesh: each browser connects peer-to-peer. Server only relays SDP/ICE.
export function useMeetRoom({ code, name, isHost, pin, roleKey, mode = "meet", audioOnly = false, authed = false, viewOnly = false,
  onChat, onWhiteboard, onDenied, onCaption, onReaction, onKicked, onForceMuted, onForceCamOff,
  onVideo, onRoomSettings, onPeerJoined, onBreakoutAnnounce, onBreakoutNotice }) {
  const selfId = useRef(stablePid(code, roleKey));
  const ws = useRef(null);
  const pcs = useRef({}); // peerId -> RTCPeerConnection
  const localStream = useRef(null); // camera + mic
  const screenTrack = useRef(null);
  const camTrack = useRef(null);
  const localVideoRef = useRef(null);
  const recorder = useRef(null);
  const chunks = useRef([]);
  const recStart = useRef(0);

  const [remotes, setRemotes] = useState([]); // [{id,name,stream,role,canSpeak,mic,cam,hand}]
  const [mic, setMic] = useState(true);
  const [cam, setCam] = useState(!audioOnly);
  const [sharing, setSharing] = useState(false);
  const [recording, setRecording] = useState(false);
  const [connected, setConnected] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [knocks, setKnocks] = useState([]);
  const [captionsOn, setCaptionsOn] = useState(false);
  const [handRaised, setHandRaised] = useState(false);
  const [lobbyOn, setLobbyOn] = useState(false);
  const [locked, setLockedState] = useState(false);
  const [myRole, setMyRole] = useState(isHost ? "host" : (["webinar", "classroom"].includes(mode) ? "attendee" : "panelist"));
  const [canSpeakSelf, setCanSpeakSelf] = useState(!(["webinar", "classroom"].includes(mode)) || isHost);
  const [spotlightId, setSpotlightId] = useState(null);
  const [activeSpeaker, setActiveSpeaker] = useState(null);
  const [breakout, setBreakout] = useState(null); // {id,name,record} when inside a breakout
  const [breakoutState, setBreakoutState] = useState(null); // manager panel snapshot
  const [qna, setQna] = useState([]);
  const [poll, setPoll] = useState(null);
  const [pollChoice, setPollChoice] = useState({}); // pollId -> my choice index
  const [quizResult, setQuizResult] = useState(null);
  const [viewerCount, setViewerCount] = useState(0);
  const [watchChat, setWatchChat] = useState([]);
  const [chatLocked, setChatLocked] = useState(false);
  const brecorder = useRef(null);
  const bchunks = useRef([]);
  const bstart = useRef(0);
  const broom = useRef(null);
  const brecordingRef = useRef(false);
  const recognition = useRef(null);
  const micR = useRef(true);
  const camR = useRef(!audioOnly);
  const handR = useRef(false);
  const speakR = useRef(canSpeakSelf);
  const activeCamTrack = useRef(null);
  const procCanvas = useRef(null);
  const procStream = useRef(null);
  const procVideo = useRef(null);
  const segRef = useRef(null);
  const rafRef = useRef(null);
  const busyRef = useRef(false);
  const bgImgRef = useRef(null);
  const bgModeRef = useRef("none");
  const [bgMode, setBgMode] = useState("none");

  const upsertRemote = useCallback((id, patch) => {
    setRemotes((prev) => {
      const clean = Object.fromEntries(Object.entries(patch).filter(([, v]) => v !== undefined && v !== null));
      const i = prev.findIndex((r) => r.id === id);
      if (i === -1) return [...prev, { id, name: clean.name || "Guest", stream: clean.stream || null, ...clean }];
      const next = [...prev];
      next[i] = { ...next[i], ...clean };
      return next;
    });
  }, []);
  const removeRemote = useCallback((id) => setRemotes((prev) => prev.filter((r) => r.id !== id)), []);

  const send = useCallback((msg) => {
    if (ws.current?.readyState === 1) ws.current.send(JSON.stringify(msg));
  }, []);

  // Disable/allow my own audio+video tracks based on speaking permission.
  const applySpeakGate = useCallback((allowed) => {
    speakR.current = allowed;
    setCanSpeakSelf(allowed);
    if (!allowed) {
      localStream.current?.getAudioTracks().forEach((t) => (t.enabled = false));
      localStream.current?.getVideoTracks().forEach((t) => (t.enabled = false));
      micR.current = false; camR.current = false; setMic(false); setCam(false);
    }
  }, []);

  const makePc = useCallback((peerId, peerName) => {
    if (pcs.current[peerId]) return pcs.current[peerId];
    const pc = new RTCPeerConnection(ICE);
    localStream.current?.getTracks().forEach((t) => pc.addTrack(t, localStream.current));
    pc.onicecandidate = (e) => { if (e.candidate) send({ type: "ice", to: peerId, candidate: e.candidate }); };
    pc.ontrack = (e) => upsertRemote(peerId, { name: peerName, stream: e.streams[0] });
    pc.onconnectionstatechange = () => {
      if (["failed", "closed", "disconnected"].includes(pc.connectionState)) { /* keep tile until peer-left */ }
    };
    pcs.current[peerId] = pc;
    upsertRemote(peerId, { name: peerName });
    return pc;
  }, [send, upsertRemote]);

  useEffect(() => {
    let closed = false;
    (async () => {
      if (!viewOnly) {
        try {
          const s = await navigator.mediaDevices.getUserMedia({ video: !audioOnly, audio: true });
          localStream.current = s;
          camTrack.current = s.getVideoTracks()[0] || null;
          activeCamTrack.current = camTrack.current;
          if (localVideoRef.current) localVideoRef.current.srcObject = s;
        } catch { /* no devices in this env — still join to relay chat/wb */ }
      }
      if (closed) return;

      const socket = new WebSocket(wsUrl(code));
      ws.current = socket;
      socket.onopen = () => {
        setConnected(true);
        send({ type: "join", id: selfId.current, name, isHost, pin, roleKey: selfId.current, authed, viewer: viewOnly });
      };
      socket.onclose = () => setConnected(false);
      socket.onmessage = async (ev) => {
        const m = JSON.parse(ev.data);
        const from = m.from;
        switch (m.type) {
          case "peers": {
            setWaiting(false);
            if (m.role) setMyRole(m.role);
            const allow = m.canSpeak !== false;
            applySpeakGate(allow);
            if (m.spotlight !== undefined) setSpotlightId(m.spotlight || null);
            for (const p of m.peers) {
              const pc = makePc(p.id, p.name);
              upsertRemote(p.id, { name: p.name, role: p.role, canSpeak: p.canSpeak });
              const offer = await pc.createOffer();
              await pc.setLocalDescription(offer);
              send({ type: "offer", to: p.id, sdp: offer });
            }
            send({ type: "media-state", name, mic: micR.current, cam: camR.current, hand: handR.current });
            break;
          }
          case "waiting": setWaiting(true); break;
          case "admitted": setWaiting(false); send({ type: "media-state", name, mic: micR.current, cam: camR.current, hand: handR.current }); break;
          case "denied": onDenied?.(m.reason); break;
          case "room-locked": setLockedState(!!m.on); break;
          case "knock": setKnocks((k) => (k.find((x) => x.id === m.id) ? k : [...k, { id: m.id, name: m.name }])); break;
          case "caption": onCaption?.({ from: m.name || "Guest", text: m.text }); break;
          case "peer-joined":
            upsertRemote(m.id, { name: m.name, role: m.role, canSpeak: m.canSpeak });
            send({ type: "media-state", name, mic: micR.current, cam: camR.current, hand: handR.current });
            onPeerJoined?.(m.id);
            break;
          case "role-changed":
            if (m.from) break;
            if (m.id === selfId.current) { setMyRole(m.role); applySpeakGate(m.canSpeak !== false); }
            else upsertRemote(m.id, { role: m.role, canSpeak: m.canSpeak });
            break;
          case "speak-granted":
            if (m.from) break;
            if (m.id === selfId.current) { speakR.current = !!m.on; setCanSpeakSelf(!!m.on); if (!m.on) applySpeakGate(false); }
            else upsertRemote(m.id, { canSpeak: !!m.on });
            break;
          case "spotlight": if (m.from) break; setSpotlightId(m.id || null); break;
          case "regroup": {
            Object.values(pcs.current).forEach((pc) => pc.close());
            pcs.current = {};
            setRemotes([]);
            setBreakout(m.group === "main" ? null : { id: m.group, name: m.groupName, record: !!m.record });
            for (const p of m.peers) {
              upsertRemote(p.id, { name: p.name, role: p.role, canSpeak: p.canSpeak });
              if (selfId.current < p.id) {
                const pc = makePc(p.id, p.name);
                const offer = await pc.createOffer();
                await pc.setLocalDescription(offer);
                send({ type: "offer", to: p.id, sdp: offer });
              }
            }
            send({ type: "media-state", name, mic: micR.current, cam: camR.current, hand: handR.current });
            const shouldRec = m.record && m.recorder === selfId.current && m.group !== "main";
            if (shouldRec && !brecordingRef.current) startBreakoutRec(m.groupName);
            else if (!shouldRec && brecordingRef.current) stopBreakoutRec();
            break;
          }
          case "breakout-state": setBreakoutState(m.state); break;
          case "breakout-announce": onBreakoutAnnounce?.(m.text); break;
          case "breakout-record-denied": onBreakoutNotice?.("denied"); break;
          case "breakout-record-warning": onBreakoutNotice?.("warn", m.rooms); break;
          case "qna-list": setQna(m.items || []); break;
          case "qna-item": setQna((x) => [...x.filter((q) => q.id !== m.item.id), m.item]); break;
          case "qna-update": setQna((x) => x.map((q) => (q.id === m.item.id ? m.item : q))); break;
          case "qna-remove": setQna((x) => x.filter((q) => q.id !== m.id)); break;
          case "poll": setPoll(m.poll); setQuizResult(null); break;
          case "poll-update": setPoll((p) => (p && p.id === m.poll.id ? m.poll : p)); break;
          case "quiz-result": setQuizResult(m); break;
          case "viewer-count": setViewerCount(m.count || 0); break;
          case "watch-chat-list": setWatchChat(m.items || []); setChatLocked(!!m.locked); break;
          case "watch-chat-item": setWatchChat((x) => [...x.filter((w) => w.id !== m.item.id), m.item].slice(-200)); break;
          case "watch-chat-remove": setWatchChat((x) => x.filter((w) => w.id !== m.id)); break;
          case "watch-chat-lock": setChatLocked(!!m.locked); break;
          case "lower-hand": setHandRaised(false); handR.current = false; send({ type: "media-state", name, mic: micR.current, cam: camR.current, hand: false }); break;
          case "video": onVideo?.(m); break;
          case "room-settings": onRoomSettings?.(m); break;
          case "offer": {
            const pc = makePc(from, m.name);
            await pc.setRemoteDescription(new RTCSessionDescription(m.sdp));
            const ans = await pc.createAnswer();
            await pc.setLocalDescription(ans);
            send({ type: "answer", to: from, sdp: ans });
            break;
          }
          case "answer": {
            const pc = pcs.current[from];
            if (pc) await pc.setRemoteDescription(new RTCSessionDescription(m.sdp));
            break;
          }
          case "ice": {
            const pc = pcs.current[from];
            if (pc && m.candidate) { try { await pc.addIceCandidate(m.candidate); } catch {} }
            break;
          }
          case "peer-left": {
            pcs.current[m.id]?.close();
            delete pcs.current[m.id];
            removeRemote(m.id);
            break;
          }
          case "chat": onChat?.({ from: m.name || "Guest", text: m.text, self: false }); break;
          case "wb": onWhiteboard?.(m.op); break;
          case "hand": upsertRemote(from, { hand: !!m.raised }); break;
          case "reaction": onReaction?.({ from: m.name || "Guest", emoji: m.emoji }); break;
          case "media-state": upsertRemote(from, { mic: m.mic, cam: m.cam, hand: m.hand }); break;
          case "force-mute": {
            localStream.current?.getAudioTracks().forEach((t) => (t.enabled = false));
            micR.current = false; setMic(false);
            send({ type: "media-state", name, mic: false, cam: camR.current, hand: handR.current });
            onForceMuted?.();
            break;
          }
          case "force-camoff": {
            localStream.current?.getVideoTracks().forEach((t) => (t.enabled = false));
            camR.current = false; setCam(false);
            send({ type: "media-state", name, mic: micR.current, cam: false, hand: handR.current });
            onForceCamOff?.();
            break;
          }
          case "kick": onKicked?.(); break;
          default: break;
        }
      };
    })();

    return () => {
      closed = true;
      try { recorder.current?.stop(); } catch {}
      try { brecorder.current?.stop(); } catch {}
      Object.values(pcs.current).forEach((pc) => pc.close());
      pcs.current = {};
      localStream.current?.getTracks().forEach((t) => t.stop());
      screenTrack.current?.stop();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      procStream.current?.getTracks().forEach((t) => t.stop());
      try { ws.current?.close(); } catch {}
    };
  }, [code]); // eslint-disable-line

  // Active-speaker detection via per-stream audio analysers.
  const acRef = useRef(null);
  useEffect(() => {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    if (!acRef.current) { try { acRef.current = new AC(); } catch { return; } }
    const ac = acRef.current;
    const nodes = [];
    const add = (id, stream) => {
      if (!stream || !stream.getAudioTracks || !stream.getAudioTracks().length) return;
      try {
        const src = ac.createMediaStreamSource(stream);
        const an = ac.createAnalyser(); an.fftSize = 512;
        src.connect(an);
        nodes.push({ id, src, an, data: new Uint8Array(an.frequencyBinCount) });
      } catch { /* stream already consumed */ }
    };
    add(selfId.current, localStream.current);
    remotes.forEach((r) => add(r.id, r.stream));
    const iv = setInterval(() => {
      let best = null, bestV = 14;
      nodes.forEach((n) => {
        n.an.getByteFrequencyData(n.data);
        const v = n.data.reduce((a, b) => a + b, 0) / n.data.length;
        if (v > bestV) { bestV = v; best = n.id; }
      });
      if (best) setActiveSpeaker(best);
    }, 400);
    return () => { clearInterval(iv); nodes.forEach((n) => { try { n.src.disconnect(); n.an.disconnect(); } catch {} }); };
  }, [remotes]);

  const toggleMic = () => {
    if (!speakR.current) return false;
    setMic((v) => { const nv = !v; localStream.current?.getAudioTracks().forEach((t) => (t.enabled = nv)); micR.current = nv; send({ type: "media-state", name, mic: nv, cam: camR.current, hand: handR.current }); return nv; });
    return true;
  };
  const toggleCam = () => {
    if (!speakR.current) return false;
    setCam((v) => { const nv = !v; localStream.current?.getVideoTracks().forEach((t) => (t.enabled = nv)); camR.current = nv; send({ type: "media-state", name, mic: micR.current, cam: nv, hand: handR.current }); return nv; });
    return true;
  };
  const raiseHand = () => setHandRaised((v) => { const nv = !v; handR.current = nv; send({ type: "hand", name, raised: nv }); return nv; });
  const sendReaction = (emoji) => { onReaction?.({ from: `${name} (You)`, emoji }); send({ type: "reaction", name, emoji }); };
  const forceMute = (id) => send({ type: "force-mute", to: id });
  const muteAll = () => remotes.forEach((r) => send({ type: "force-mute", to: r.id }));
  const removePeer = (id) => send({ type: "kick", to: id });
  const setLobby = (on) => { setLobbyOn(on); send({ type: "set-lobby", on }); };
  const lockRoom = (on) => { setLockedState(on); send({ type: "set-locked", on }); };
  const setRole = (id, role) => send({ type: "set-role", to: id, role });
  const grantSpeak = (id, on) => send({ type: "grant-speak", to: id, on });
  const spotlight = (id) => send({ type: "spotlight", to: id });
  const clearSpotlight = () => send({ type: "spotlight", to: null });
  const createBreakouts = (rooms, record) => send({ type: "breakout-create", rooms, record });
  const assignBreakout = (id, room) => send({ type: "breakout-assign", to: id, room });
  const openBreakouts = () => send({ type: "breakout-open" });
  const closeBreakouts = () => send({ type: "breakout-close" });
  const joinBreakout = (room) => send({ type: "breakout-join", room });
  const breakoutMessage = (text) => send({ type: "breakout-message", text });
  const askQuestion = (text) => send({ type: "qna-ask", text });
  const voteQuestion = (id) => send({ type: "qna-vote", id });
  const answerQuestion = (id, answer) => send({ type: "qna-answer", id, answer });
  const dismissQuestion = (id) => send({ type: "qna-dismiss", id });
  const startPoll = (question, options) => send({ type: "poll-start", question, options });
  const startQuiz = (question, options, correct) => send({ type: "quiz-start", question, options, correct });
  const votePoll = (id, choice) => { setPollChoice((m) => ({ ...m, [id]: choice })); send({ type: "poll-vote", id, choice }); };
  const closePoll = (id) => send({ type: "poll-close", id });
  const lowerHand = (id) => send({ type: "lower-hand", to: id });
  const sendWatchChat = (text) => { const t = (text || "").trim(); if (t) send({ type: "watch-chat", name, text: t }); };
  const deleteWatchChat = (id) => send({ type: "watch-chat-delete", id });
  const lockWatchChat = (locked) => { setChatLocked(locked); send({ type: "watch-chat-lock", locked }); };

  function startBreakoutRec(roomName) {
    if (brecordingRef.current) return;
    const videoTrack = sharing ? screenTrack.current : (activeCamTrack.current || camTrack.current);
    const audio = localStream.current?.getAudioTracks()[0];
    const tracks = [videoTrack, audio].filter(Boolean);
    if (!tracks.length) return;
    const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus") ? "video/webm;codecs=vp9,opus" : "video/webm";
    const rec = new MediaRecorder(new MediaStream(tracks), { mimeType: mime });
    bchunks.current = [];
    rec.ondataavailable = (e) => { if (e.data.size) bchunks.current.push(e.data); };
    rec.start(1000);
    brecorder.current = rec; bstart.current = Date.now(); broom.current = roomName; brecordingRef.current = true;
  }
  async function stopBreakoutRec() {
    const rec = brecorder.current;
    if (!rec) return;
    const done = new Promise((res) => { rec.onstop = res; });
    rec.stop();
    await done;
    brecorder.current = null; brecordingRef.current = false;
    const blob = new Blob(bchunks.current, { type: "video/webm" });
    const dur = Math.round((Date.now() - bstart.current) / 1000);
    const fd = new FormData();
    fd.append("file", blob, "breakout.webm");
    fd.append("title", `Breakout: ${broom.current || "Room"}`);
    fd.append("duration_seconds", String(dur));
    fd.append("room_name", broom.current || "");
    try { await api.post(`/meet/${code}/breakout-recording`, fd, { headers: { "Content-Type": "multipart/form-data" } }); } catch { /* upload failed */ }
  }

  const replaceVideoForAll = (track) => {
    Object.values(pcs.current).forEach((pc) => {
      const sender = pc.getSenders().find((s) => s.track && s.track.kind === "video");
      if (sender) sender.replaceTrack(track);
    });
  };

  const toggleShare = async () => {
    if (!sharing) {
      try {
        const disp = await navigator.mediaDevices.getDisplayMedia({ video: true });
        const track = disp.getVideoTracks()[0];
        screenTrack.current = track;
        track.onended = () => stopShare();
        replaceVideoForAll(track);
        if (localVideoRef.current) localVideoRef.current.srcObject = disp;
        setSharing(true);
      } catch { /* cancelled */ }
    } else stopShare();
  };
  const stopShare = () => {
    screenTrack.current?.stop();
    screenTrack.current = null;
    if (activeCamTrack.current) replaceVideoForAll(activeCamTrack.current);
    if (localVideoRef.current) localVideoRef.current.srcObject = (bgModeRef.current !== "none" && procStream.current) ? procStream.current : localStream.current;
    setSharing(false);
  };

  const stopBgLoop = () => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    procStream.current?.getTracks().forEach((t) => t.stop());
    procStream.current = null;
  };

  const setBackground = async (bgm, imageUrl) => {
    if (!camTrack.current) return false;
    if (bgm === "none") {
      stopBgLoop();
      bgModeRef.current = "none"; setBgMode("none");
      activeCamTrack.current = camTrack.current;
      if (!sharing) { replaceVideoForAll(camTrack.current); if (localVideoRef.current) localVideoRef.current.srcObject = localStream.current; }
      return true;
    }
    let SS;
    try { await loadMP(); SS = window.SelfieSegmentation; } catch { return false; }
    if (!SS) return false;
    if (!procVideo.current) { procVideo.current = document.createElement("video"); procVideo.current.muted = true; procVideo.current.playsInline = true; }
    procVideo.current.srcObject = new MediaStream([camTrack.current]);
    try { await procVideo.current.play(); } catch { /* ignore */ }
    const st = camTrack.current.getSettings();
    const w = st.width || 640, h = st.height || 480;
    if (!procCanvas.current) procCanvas.current = document.createElement("canvas");
    procCanvas.current.width = w; procCanvas.current.height = h;
    const ctx = procCanvas.current.getContext("2d");
    if (bgm === "image" && imageUrl) { const img = new Image(); img.crossOrigin = "anonymous"; img.src = imageUrl; bgImgRef.current = img; }
    if (!segRef.current) {
      const seg = new SS({ locateFile: (f) => `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${f}` });
      seg.setOptions({ modelSelection: 1, selfieMode: true });
      seg.onResults((res) => {
        const cw = procCanvas.current.width, ch = procCanvas.current.height;
        ctx.save();
        ctx.clearRect(0, 0, cw, ch);
        ctx.drawImage(res.segmentationMask, 0, 0, cw, ch);
        ctx.globalCompositeOperation = "source-in";
        ctx.drawImage(res.image, 0, 0, cw, ch);
        ctx.globalCompositeOperation = "destination-over";
        if (bgModeRef.current === "image" && bgImgRef.current && bgImgRef.current.complete) {
          ctx.drawImage(bgImgRef.current, 0, 0, cw, ch);
        } else {
          ctx.filter = "blur(14px)";
          ctx.drawImage(res.image, 0, 0, cw, ch);
          ctx.filter = "none";
        }
        ctx.restore();
      });
      segRef.current = seg;
    }
    bgModeRef.current = bgm; setBgMode(bgm);
    if (!procStream.current) {
      procStream.current = procCanvas.current.captureStream(24);
      activeCamTrack.current = procStream.current.getVideoTracks()[0];
      if (!sharing) { replaceVideoForAll(activeCamTrack.current); if (localVideoRef.current) localVideoRef.current.srcObject = procStream.current; }
    }
    const loop = async () => {
      if (bgModeRef.current === "none") return;
      if (!busyRef.current && procVideo.current && procVideo.current.readyState >= 2) {
        busyRef.current = true;
        try { await segRef.current.send({ image: procVideo.current }); } catch { /* ignore frame */ }
        busyRef.current = false;
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    if (!rafRef.current) loop();
    return true;
  };

  const startRecording = () => {
    const videoTrack = sharing ? screenTrack.current : (activeCamTrack.current || camTrack.current);
    const audio = localStream.current?.getAudioTracks()[0];
    const tracks = [videoTrack, audio].filter(Boolean);
    if (!tracks.length) return false;
    const stream = new MediaStream(tracks);
    const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus") ? "video/webm;codecs=vp9,opus" : "video/webm";
    const rec = new MediaRecorder(stream, { mimeType: mime });
    chunks.current = [];
    rec.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
    rec.start(1000);
    recorder.current = rec;
    recStart.current = Date.now();
    setRecording(true);
    return true;
  };

  const stopRecording = async (title, reference) => {
    const rec = recorder.current;
    if (!rec) return null;
    const done = new Promise((res) => { rec.onstop = res; });
    rec.stop();
    await done;
    setRecording(false);
    recorder.current = null;
    const blob = new Blob(chunks.current, { type: "video/webm" });
    const dur = Math.round((Date.now() - recStart.current) / 1000);
    const fd = new FormData();
    fd.append("file", blob, "recording.webm");
    fd.append("title", title || "Meeting recording");
    fd.append("duration_seconds", String(dur));
    fd.append("reference", reference || "");
    const { data } = await api.post("/recordings/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.recording_id;
  };

  const sendChat = (text) => { onChat?.({ from: "You", text, self: true }); send({ type: "chat", name, text }); };
  const sendWhiteboard = (op) => send({ type: "wb", op });

  const admit = (id) => { send({ type: "admit", to: id }); setKnocks((k) => k.filter((x) => x.id !== id)); };
  const deny = (id) => { send({ type: "deny", to: id }); setKnocks((k) => k.filter((x) => x.id !== id)); };

  const toggleCaptions = () => {
    if (captionsOn) { try { recognition.current?.stop(); } catch {} recognition.current = null; setCaptionsOn(false); return true; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return false;
    const rec = new SR();
    rec.continuous = true; rec.interimResults = true; rec.lang = "en-US";
    rec.onresult = (e) => {
      let t = "";
      for (let i = e.resultIndex; i < e.results.length; i++) t += e.results[i][0].transcript;
      t = t.trim();
      if (t) { onCaption?.({ from: `${name} (You)`, text: t }); send({ type: "caption", name, text: t }); }
    };
    rec.onend = () => { if (recognition.current === rec) { try { rec.start(); } catch {} } };
    try { rec.start(); } catch {}
    recognition.current = rec; setCaptionsOn(true); return true;
  };

  useEffect(() => () => { try { recognition.current?.stop(); } catch {} }, []);

  return {
    selfId: selfId.current,
    localVideoRef, remotes, mic, cam, sharing, recording, connected, waiting, knocks, captionsOn,
    handRaised, lobbyOn, locked, myRole, canSpeakSelf, spotlightId, activeSpeaker,
    breakout, breakoutState, qna, poll, pollChoice, quizResult,
    viewerCount, watchChat, chatLocked, sendWatchChat, deleteWatchChat, lockWatchChat,
    toggleMic, toggleCam, toggleShare, startRecording, stopRecording, admit, deny, toggleCaptions,
    raiseHand, sendReaction, forceMute, muteAll, removePeer, setLobby, lockRoom,
    setRole, grantSpeak, spotlight, clearSpotlight,
    createBreakouts, assignBreakout, openBreakouts, closeBreakouts, joinBreakout, breakoutMessage,
    askQuestion, voteQuestion, answerQuestion, dismissQuestion, startPoll, startQuiz, votePoll, closePoll, lowerHand,
    sendChat, sendWhiteboard, participantCount: remotes.length + 1,
    bgMode, setBackground,
    sendVideo: (p) => send({ type: "video", ...p }),
    sendRoomSettings: (p) => send({ type: "room-settings", ...p }),
  };
}
